import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';

export default function TripsDashboard() {
  const { user, logout } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newTripName, setNewTripName] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      loadTrips();
    }
  }, [user]);

  const loadTrips = async () => {
    try {
      setLoading(true);
      const tripsData = await api.listTripsForUser(user.id);
      setTrips(tripsData || []);
    } catch (err) {
      setError('Failed to load trips');
      console.error('Error loading trips:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrip = async (e) => {
    e.preventDefault();
    if (!newTripName.trim()) return;

    try {
      setCreating(true);
      const trip = await api.createTrip(newTripName.trim(), user.id);
      await api.joinTrip(trip.id, user.id);
      setNewTripName('');
      await loadTrips();
    } catch (err) {
      setError('Failed to create trip');
      console.error('Error creating trip:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleTripClick = (tripId) => {
    navigate(`/trip/${tripId}`);
  };

  const containerStyle = {
    minHeight: '100vh',
    background: '#1a1a1c',
    color: '#e8e6e3',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const headerStyle = {
    background: '#2a2a2e',
    borderBottom: '1px solid #3a3a3e',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const userInfoStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const userNameStyle = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#e8e6e3',
  };

  const logoutButtonStyle = {
    padding: '8px 16px',
    background: 'transparent',
    border: '1px solid #3a3a3e',
    borderRadius: '4px',
    color: '#999',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  };

  const contentStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '32px 24px',
  };

  const titleStyle = {
    fontSize: '28px',
    fontWeight: 600,
    marginBottom: '24px',
  };

  const createFormStyle = {
    display: 'flex',
    gap: '12px',
    marginBottom: '32px',
    padding: '20px',
    background: '#2a2a2e',
    borderRadius: '8px',
    border: '1px solid #3a3a3e',
  };

  const inputStyle = {
    flex: 1,
    padding: '10px 14px',
    background: '#1a1a1c',
    border: '1px solid #3a3a3e',
    borderRadius: '4px',
    color: '#e8e6e3',
    fontSize: '14px',
  };

  const createButtonStyle = {
    padding: '10px 20px',
    background: '#e8a87c',
    color: '#1a1a1c',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: creating ? 'not-allowed' : 'pointer',
    opacity: creating ? 0.6 : 1,
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  };

  const tripCardStyle = {
    padding: '20px',
    background: '#2a2a2e',
    border: '1px solid #3a3a3e',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  const tripCardHoverStyle = {
    ...tripCardStyle,
    borderColor: '#e8a87c',
    transform: 'translateY(-2px)',
  };

  const tripNameStyle = {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '8px',
    color: '#e8e6e3',
  };

  const tripMetaStyle = {
    fontSize: '12px',
    color: '#999',
  };

  const errorStyle = {
    padding: '12px',
    background: '#d4726a22',
    border: '1px solid #d4726a44',
    borderRadius: '4px',
    color: '#d4726a',
    fontSize: '13px',
    marginBottom: '16px',
  };

  const emptyStateStyle = {
    textAlign: 'center',
    padding: '64px 24px',
    color: '#666',
  };

  const emptyStateTextStyle = {
    fontSize: '16px',
    marginBottom: '8px',
  };

  const loadingStyle = {
    textAlign: 'center',
    padding: '64px 24px',
    color: '#666',
    fontSize: '16px',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <div style={userInfoStyle}>
          <span style={userNameStyle}>{user?.name}</span>
        </div>
        <button onClick={logout} style={logoutButtonStyle}>
          Log Out
        </button>
      </header>

      <div style={contentStyle}>
        <h1 style={titleStyle}>My Trips</h1>

        {error && (
          <div style={errorStyle}>{error}</div>
        )}

        <form onSubmit={handleCreateTrip} style={createFormStyle}>
          <input
            type="text"
            value={newTripName}
            onChange={(e) => setNewTripName(e.target.value)}
            placeholder="New trip name..."
            disabled={creating}
            style={inputStyle}
          />
          <button type="submit" disabled={creating} style={createButtonStyle}>
            {creating ? 'Creating...' : 'Create Trip'}
          </button>
        </form>

        {loading ? (
          <div style={loadingStyle}>Loading trips...</div>
        ) : trips.length === 0 ? (
          <div style={emptyStateStyle}>
            <div style={emptyStateTextStyle}>No trips yet</div>
            <div style={{ fontSize: '14px' }}>Create your first trip to get started</div>
          </div>
        ) : (
          <div style={gridStyle}>
            {trips.map((trip) => (
              <div
                key={trip.id}
                onClick={() => handleTripClick(trip.id)}
                style={tripCardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#e8a87c';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#3a3a3e';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={tripNameStyle}>{trip.name}</div>
                <div style={tripMetaStyle}>
                  Created {new Date(trip.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
